import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useT } from "../context/LanguageContext";
import { BookOpen, CheckCircle2, Circle } from "lucide-react";

export default function StudentCurriculumPage() {
  const t = useT();
  const [weeks, setWeeks] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [solo, setSolo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/student/curriculum");
      setWeeks(r.data.weeks || []);
      setProgress(r.data.progress || {});
      setSolo(!!r.data.solo_learner);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isDone = (weekId, idx) => progress[weekId]?.[String(idx)]?.completed === true;

  const toggle = async (weekId, idx) => {
    const completed = !isDone(weekId, idx);
    // optimistic
    setProgress((p) => ({
      ...p,
      [weekId]: { ...(p[weekId] || {}), [String(idx)]: { completed, completed_at: completed ? new Date().toISOString() : null } },
    }));
    try {
      await apiClient.put("/student/curriculum/progress", { week_id: weekId, focus_point_idx: idx, completed });
    } catch (e) {
      toast.error(t("Couldn't save — reverting."));
      load();
    }
  };

  if (loading) return <div>{t("Loading…")}</div>;
  if (solo) {
    return (
      <div className="space-y-4" data-testid="curriculum-solo">
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BookOpen className="w-8 h-8" /> {t("Curriculum")}</h1>
        <div className="bento-card p-8 text-center">
          <p style={{ color: "var(--app-text-muted)" }}>
            {t("You're learning on your own — there's no teacher curriculum yet. If you join a teacher's studio with an invite code, their weeks will show up here.")}
          </p>
        </div>
      </div>
    );
  }

  if (!weeks.length) {
    return (
      <div className="space-y-4" data-testid="curriculum-empty">
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BookOpen className="w-8 h-8" /> {t("Curriculum")}</h1>
        <div className="bento-card p-8 text-center">
          <p style={{ color: "var(--app-text-muted)" }}>{t("Your teacher hasn't published any weeks yet. Check back soon!")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-curriculum-page">
      <div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BookOpen className="w-8 h-8" /> {t("Curriculum")}</h1>
        <p style={{ color: "var(--app-text-muted)" }}>
          {t("Your teacher's plan, one week at a time. Tap each goal once you've nailed it; your teacher sees your progress automatically.")}
        </p>
      </div>

      {weeks.map((w) => {
        const weekDone = isDone(w.id, -1);
        const focusCount = (w.focus_points || []).length;
        const focusDone = (w.focus_points || []).filter((_, i) => isDone(w.id, i)).length;
        return (
          <section key={w.id} className="bento-card p-5" data-testid={`cur-week-${w.id}`}>
            <header className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="inline-flex items-center justify-center rounded-xl w-12 h-12 shrink-0"
                      style={{ background: weekDone ? "var(--app-accent)" : "var(--app-surface-2)",
                               color: weekDone ? "#0a0a0a" : "var(--app-text-muted)" }}>
                  {weekDone ? <CheckCircle2 className="w-6 h-6" /> : <span className="font-display font-bold">{w.week_number}</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-xl leading-tight">{w.title}</div>
                  {focusCount > 0 && (
                    <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>
                      {focusDone}/{focusCount} {t("goals complete")}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggle(w.id, -1)}
                data-testid={`week-done-${w.id}`}
                className={`btn-pill text-sm font-semibold ${weekDone ? "btn-accent" : "btn-outline"}`}
                aria-pressed={weekDone}
              >
                {weekDone ? t("Week complete ✓") : t("Mark week complete")}
              </button>
            </header>

            {w.notes && <p className="text-sm mb-3" style={{ color: "var(--app-text-muted)" }}>{w.notes}</p>}

            {focusCount > 0 && (
              <ul className="space-y-2">
                {w.focus_points.map((fp, i) => {
                  const done = isDone(w.id, i);
                  return (
                    <li key={i} className="flex items-start gap-3 rounded-xl px-3 py-2 border"
                        style={{ borderColor: done ? "var(--app-accent)" : "var(--app-border)", background: done ? "rgba(255,183,3,0.08)" : "transparent" }}
                        data-testid={`cur-focus-${w.id}-${i}`}>
                      <Checkbox
                        checked={done}
                        onCheckedChange={() => toggle(w.id, i)}
                        id={`cp-${w.id}-${i}`}
                        data-testid={`cur-focus-toggle-${w.id}-${i}`}
                      />
                      <label htmlFor={`cp-${w.id}-${i}`} className={`flex-1 text-sm cursor-pointer ${done ? "line-through" : ""}`}
                             style={{ color: done ? "var(--app-text-muted)" : "var(--app-text)" }}>
                        {fp}
                      </label>
                      {done && <CheckCircle2 className="w-4 h-4" style={{ color: "var(--app-accent)" }} />}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
